/* Hero background video — deferred loader.
   HOMEPAGE_ARCHITECTURE.md §"Hero video".

   The <video> in slideshow.liquid ships with no src and no autoplay attribute
   on purpose. Either one makes the browser fetch the file while the page is
   still painting, which puts several megabytes in front of the hero image and
   moves LCP out by seconds. This attaches the source only once the page has
   finished loading, so the image paints first and the video arrives over it.

   Three cases deliberately never load it at all:
     - reduced motion requested
     - the element is display:none when the mobile setting is off
     - the visitor asked the browser to save data, or is on a 2g connection

   `?herovideo=force` skips the first and third so a support case can be told
   apart from a device that simply refuses to autoplay. It cannot force a
   hidden element, and it changes nothing for normal visitors. */
(function () {
  'use strict';

  var started = new WeakSet();

  var forced = /[?&]herovideo=force/.test(location.search);

  function tooExpensive() {
    var c = navigator.connection;
    if (!c) return false;
    // saveData is an explicit request from the visitor, so it is always honoured.
    if (c.saveData) return true;
    // effectiveType is NOT the connection type — Chrome derives it from observed
    // round-trip times and routinely reports "3g" on perfectly good WiFi. Using
    // it to block 3g silently denied the video to ordinary visitors. Only the
    // genuinely slow tiers are excluded now.
    return /(^|-)(slow-2g|2g)$/.test(c.effectiveType || '');
  }

  function start(video) {
    if (started.has(video)) return;

    // offsetParent is null for a display:none ancestor, which is exactly how the
    // theme hides the desktop container on a phone. Cheaper and more honest than
    // duplicating the breakpoint in JS, where it would drift from the CSS.
    if (video.offsetParent === null) return;

    var src = video.getAttribute('data-src');
    if (!src) return;

    started.add(video);

    // iOS ignores a muted ATTRIBUTE set before the element is live and will
    // refuse to autoplay; the property is what it actually reads.
    video.muted = true;
    video.src = src;
    // preload="none" means the element holds no buffered data; Safari wants the
    // explicit load() before it will honour play() on a freshly assigned src.
    video.load();

    video.addEventListener('playing', function () {
      video.classList.add('is-playing');
    }, { once: true });

    tryPlay(video);
  }

  // A rejected play() is a normal outcome, not an error — the image below
  // stays visible and the hero still looks finished. The usual cause is an
  // iPhone in Low Power Mode (or Android battery saver), which refuses muted
  // autoplay but allows play() inside a user gesture. So a refused video is
  // retried on the visitor's first tap, click or key press.
  function tryPlay(video) {
    var attempt = video.play();
    if (attempt && attempt.catch) attempt.catch(armGestureRetry);
  }

  var gestureArmed = false;
  function armGestureRetry() {
    if (gestureArmed) return;
    gestureArmed = true;
    var events = ['pointerdown', 'touchend', 'keydown'];
    function retry() {
      events.forEach(function (t) { document.removeEventListener(t, retry, true); });
      gestureArmed = false;
      document.querySelectorAll('[data-hero-video]').forEach(function (v) {
        if (started.has(v) && v.paused && v.offsetParent !== null) tryPlay(v);
      });
    }
    events.forEach(function (t) { document.addEventListener(t, retry, { capture: true, passive: true }); });
  }

  /* The phone and desktop containers swap at the theme's breakpoint. A video
     hidden at load was skipped on purpose, but if the viewport then crosses
     the breakpoint — a laptop window snapped to half the screen and then
     maximised, a tablet rotated — the newly visible one never started, and
     the visitor saw only the image. Measured on live: loaded at 600px, widened
     to 1280px, desktop video had no src. Re-run on resize; the one that went
     hidden is paused so it stops decoding off-screen. */
  var resizeTimer = null;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      document.querySelectorAll('[data-hero-video]').forEach(function (v) {
        if (!started.has(v)) return;
        if (v.offsetParent === null) { if (!v.paused) v.pause(); }
        else if (v.paused) tryPlay(v);
      });
      init(document);
    }, 200);
  }

  function init(root) {
    if (!forced && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!forced && tooExpensive()) return;
    (root || document).querySelectorAll('[data-hero-video]').forEach(start);
  }

  var ready = false;
  function onReady() {
    if (ready) return;
    ready = true;
    init(document);
    window.addEventListener('resize', onResize);
  }

  /* `load` waits for EVERY resource, third-party scripts included. One slow
     tracker or app script holds the event back, and the hero sits on its
     image the whole time. So the video also starts 3s after DOMContentLoaded,
     whichever comes first — by then the hero image has long since painted. */
  if (document.readyState === 'complete') onReady();
  else {
    window.addEventListener('load', onReady);
    var afterDom = function () { setTimeout(onReady, 3000); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', afterDom);
    else afterDom();
  }

  // Theme editor: the section is re-rendered on every settings change, so the
  // new element needs the same treatment without a page reload.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
